import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Checkbox, Input, Select, Space, Table, Tag, Typography, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';

import { resolveFrontendApiBase } from '../utils/apiBase';
import adminAxios from './adminAxios';
import './AdminDashboardSurfaces.css';

const API_BASE = resolveFrontendApiBase();
const TUTORIAL_COMMAND_TIMEOUT_MS = 600000;
const DEFAULT_TUTORIAL_VOICE_ID = 'tnSpp4vdxKPjI9w0GnoV';
const { Paragraph, Text, Title } = Typography;

type TutorialFlowSummary = {
  id?: string;
  title?: string;
  route?: string;
  fixture?: string;
  source?: string | null;
  sceneCount?: number;
  artifactRoot?: string;
  lastRunAt?: string | null;
  traceStatus?: string | null;
  storyboardStatus?: string | null;
  finalNarrationStatus?: string | null;
  renderManifestStatus?: string | null;
  voiceoverStatus?: string | null;
  videoRenderStatus?: string | null;
  finalVideoPath?: string | null;
};

type TutorialArtifact = {
  path: string;
  sizeBytes: number;
  modifiedAt: string;
};

type TutorialStatusResponse = {
  defaultTutorial: string;
  defaultBaseUrl: string;
  runnerAvailable: boolean;
  validatorAvailable: boolean;
  storyboardGeneratorAvailable: boolean;
  renderManifestBuilderAvailable: boolean;
  videoRendererAvailable: boolean;
  documentGeneratorAvailable?: boolean;
  manifestAvailable: boolean;
  flowAvailable: boolean;
  flows: TutorialFlowSummary[];
  artifactRoot: string;
  traceStatus?: string | null;
  timingStatus?: string | null;
  storyboardStatus?: string | null;
  finalNarrationStatus?: string | null;
  renderManifestStatus?: string | null;
  voiceoverStatus?: string | null;
  videoRenderStatus?: string | null;
  trace?: {
    status?: string;
    duration_ms?: number;
    scenes?: Array<{ id?: string; status?: string; screenshots?: string[] }>;
  } | null;
  timingDiagnostics?: {
    status?: string;
    duration_ms?: number;
    error?: string;
  } | null;
  storyboard?: {
    status?: string;
    estimated_duration_seconds?: number;
    scenes?: Array<{ id?: string; narration_draft?: string; screenshot?: string }>;
  } | null;
  renderManifest?: {
    status?: string;
    duration_seconds?: number;
    timeline?: Array<{ id?: string; screenshot?: string; narration?: string }>;
  } | null;
  voiceover?: {
    status?: string;
    provider?: string;
    model_id?: string;
    voice_id?: string;
    track_count?: number;
    scene_track_count?: number;
  } | null;
  renderResult?: {
    status?: string;
    output?: string;
    output_size_bytes?: number;
    duration_seconds?: number;
  } | null;
  artifacts: TutorialArtifact[];
};

type CommandResult = {
  command: string[];
  returnCode: number;
  stdout: string;
  stderr: string;
};

type TutorialVoiceOption = {
  voiceId: string;
  name?: string | null;
  category?: string | null;
  description?: string | null;
  previewUrl?: string | null;
  labels?: Record<string, string>;
  sampleCount?: number;
  accent?: string | null;
  gender?: string | null;
  age?: string | null;
  useCase?: string | null;
};

type TutorialVoiceListResponse = {
  status: 'succeeded' | 'missing_api_key';
  provider: string;
  defaultVoiceId?: string;
  defaultVoiceSource?: string;
  voices: TutorialVoiceOption[];
  error?: string;
};

type TutorialRunResponse = {
  status: 'succeeded' | 'failed';
  tutorial: string;
  baseUrl: string;
  run: CommandResult;
  validation?: CommandResult | null;
  summary: TutorialStatusResponse;
};

type TutorialStoryboardResponse = {
  status: 'succeeded' | 'failed';
  tutorial: string;
  storyboard: CommandResult;
  validation?: CommandResult | null;
  summary: TutorialStatusResponse;
};

type TutorialRenderPackageResponse = {
  status: 'succeeded' | 'failed';
  tutorial: string;
  renderPackage: CommandResult;
  validation?: CommandResult | null;
  summary: TutorialStatusResponse;
};

type TutorialVideoRenderResponse = {
  status: 'succeeded' | 'failed';
  tutorial: string;
  voiceId?: string | null;
  render: CommandResult;
  validation?: CommandResult | null;
  summary: TutorialStatusResponse;
};

type TutorialDefinitionValidationResponse = {
  status: 'succeeded' | 'failed';
  validation: CommandResult;
  summary: TutorialStatusResponse;
};

type TutorialReviewItem = {
  key: string;
  label: string;
  path: string;
  purpose: string;
  newArtifact?: boolean;
  previewable?: boolean;
  primary?: boolean;
};

const formatBytes = (size: number): string => {
  if (!Number.isFinite(size)) return '';
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const downloadBlob = (blob: Blob, filename: string): void => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

const statusTagColor = (status?: string | null): string => {
  if (!status) return 'default';
  if (['succeeded', 'ready', 'draft', 'complete'].includes(status)) return 'green';
  if (status === 'failed') return 'red';
  if (['running', 'queued'].includes(status)) return 'blue';
  return 'default';
};

const AdminTutorialsTab: React.FC = () => {
  const [status, setStatus] = useState<TutorialStatusResponse | null>(null);
  const [runResult, setRunResult] = useState<TutorialRunResponse | null>(null);
  const [tutorialId, setTutorialId] = useState('upload-first-book');
  const [baseUrl, setBaseUrl] = useState(
    typeof window !== 'undefined' && window.location.hostname === 'localhost'
      ? window.location.origin
      : 'http://127.0.0.1:3000'
  );
  const [recordVideo, setRecordVideo] = useState(false);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [storyboarding, setStoryboarding] = useState(false);
  const [buildingRenderPackage, setBuildingRenderPackage] = useState(false);
  const [renderingVideo, setRenderingVideo] = useState(false);
  const [downloadingPath, setDownloadingPath] = useState<string | null>(null);
  const [storyboardResult, setStoryboardResult] = useState<TutorialStoryboardResponse | null>(null);
  const [renderPackageResult, setRenderPackageResult] = useState<TutorialRenderPackageResponse | null>(null);
  const [videoRenderResult, setVideoRenderResult] = useState<TutorialVideoRenderResponse | null>(null);
  const [definitionValidationResult, setDefinitionValidationResult] = useState<TutorialDefinitionValidationResponse | null>(null);
  const [validatingDefinitions, setValidatingDefinitions] = useState(false);
  const [previewVideoUrl, setPreviewVideoUrl] = useState<string | null>(null);
  const [previewVideoPath, setPreviewVideoPath] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [voiceOptions, setVoiceOptions] = useState<TutorialVoiceOption[]>([]);
  const [selectedVoiceId, setSelectedVoiceId] = useState(DEFAULT_TUTORIAL_VOICE_ID);
  const [voicesLoading, setVoicesLoading] = useState(false);
  const [voiceLoadError, setVoiceLoadError] = useState<string | null>(null);
  const [tutorialLibraryExpanded, setTutorialLibraryExpanded] = useState(false);

  const selectedFlow = useMemo(
    () => status?.flows.find((flow) => flow.id === tutorialId),
    [status?.flows, tutorialId]
  );
  const isGeneratedDocumentTutorial = selectedFlow?.source === 'generated_document';
  const runActionLabel = isGeneratedDocumentTutorial ? 'Generate Document Flow' : 'Run Golden Flow';
  const clearVideoPreview = (): void => {
    setPreviewVideoUrl((currentUrl) => {
      if (currentUrl) {
        window.URL.revokeObjectURL(currentUrl);
      }
      return null;
    });
    setPreviewVideoPath(null);
  };

  useEffect(() => {
    return () => {
      if (previewVideoUrl) {
        window.URL.revokeObjectURL(previewVideoUrl);
      }
    };
  }, [previewVideoUrl]);

  useEffect(() => {
    setPreviewVideoUrl((currentUrl) => {
      if (currentUrl) {
        window.URL.revokeObjectURL(currentUrl);
      }
      return null;
    });
    setPreviewVideoPath(null);
  }, [tutorialId]);

  const artifactsByPath = useMemo(() => {
    const map = new Map<string, TutorialArtifact>();
    (status?.artifacts || []).forEach((artifact) => {
      map.set(artifact.path, artifact);
    });
    return map;
  }, [status?.artifacts]);

  const finalVideoPath = status?.renderResult?.output || `renders/${tutorialId}.mp4`;
  const selectedVoice = useMemo(
    () => voiceOptions.find((voice) => voice.voiceId === selectedVoiceId) || null,
    [selectedVoiceId, voiceOptions]
  );
  const voiceSelectOptions = useMemo(
    () =>
      voiceOptions.map((voice) => {
        const detail = [voice.accent, voice.gender, voice.useCase].filter(Boolean).join(' · ');
        return {
          value: voice.voiceId,
          label: `${voice.name || voice.voiceId}${detail ? ` (${detail})` : ''}`,
        };
      }),
    [voiceOptions]
  );
  const tutorialSelectOptions = useMemo(
    () =>
      (status?.flows || []).map((flow) => ({
        value: flow.id || '',
        label: `${flow.title || flow.id}${flow.sceneCount ? ` (${flow.sceneCount} scenes)` : ''}`,
      })),
    [status?.flows]
  );

  const reviewItems = useMemo<TutorialReviewItem[]>(
    () => [
      {
        key: 'final-video',
        label: 'Final video',
        path: finalVideoPath,
        purpose: 'Watch this first. It is the assembled tutorial with intro, TTS, scene timing, closing, and captions.',
        previewable: true,
        primary: true,
      },
      {
        key: 'voiceover-script',
        label: 'Voiceover script',
        path: 'voiceover/voiceover_script.md',
        purpose: 'Review the exact intro and scene narration text sent to TTS.',
        newArtifact: true,
      },
      {
        key: 'closing-narration',
        label: 'Closing narration',
        path: 'closing/closing_narration.txt',
        purpose: 'Review the final spoken wrap-up appended after the tutorial scenes.',
        newArtifact: true,
      },
      {
        key: 'final-narration',
        label: 'Final narration',
        path: 'final_narration.json',
        purpose: 'Review scene-level captions, narration text, and planned durations before TTS/render.',
      },
      {
        key: 'voiceover-manifest',
        label: 'TTS manifest',
        path: 'voiceover_manifest.json',
        purpose: 'Review provider, model, voice, request IDs, audio paths, and measured audio durations.',
        newArtifact: true,
      },
      {
        key: 'render-result',
        label: 'Render result',
        path: 'render_result.json',
        purpose: 'Review final output metadata, ffmpeg command results, media probe, and timeline after render.',
      },
      {
        key: 'storyboard',
        label: 'Storyboard',
        path: 'storyboard.json',
        purpose: 'Use when the video content feels wrong and you need to inspect the source scene plan.',
      },
    ],
    [finalVideoPath]
  );

  const loadStatus = async (requestedTutorialId = tutorialId): Promise<void> => {
    try {
      setLoading(true);
      const { data } = await adminAxios.get<TutorialStatusResponse>(
        `${API_BASE}/admin/tutorials/golden-flow/status`,
        { params: { tutorial: requestedTutorialId } }
      );
      setStatus(data);
      if (data.defaultTutorial && requestedTutorialId === 'upload-first-book') {
        setTutorialId(data.defaultTutorial);
      }
      if (data.defaultBaseUrl && baseUrl === 'http://127.0.0.1:3000') {
        setBaseUrl(data.defaultBaseUrl);
      }
      if (!selectedVoiceId) {
        setSelectedVoiceId(DEFAULT_TUTORIAL_VOICE_ID);
      }
    } catch (error: any) {
      message.error(String(error?.response?.data?.detail || error?.message || 'Failed to load tutorial status.'));
    } finally {
      setLoading(false);
    }
  };

  const loadVoiceOptions = async (): Promise<void> => {
    try {
      setVoicesLoading(true);
      setVoiceLoadError(null);
      const { data } = await adminAxios.get<TutorialVoiceListResponse>(
        `${API_BASE}/admin/tutorials/tts/voices`,
        { timeout: 30000 }
      );
      setVoiceOptions(data.voices || []);
      if (!selectedVoiceId) {
        setSelectedVoiceId(data.defaultVoiceId || data.voices?.[0]?.voiceId || '');
      }
      if (data.status === 'missing_api_key') {
        setVoiceLoadError(data.error || 'ElevenLabs API key is not configured.');
      }
    } catch (error: any) {
      setVoiceLoadError(String(error?.response?.data?.detail || error?.message || 'Failed to load ElevenLabs voices.'));
    } finally {
      setVoicesLoading(false);
    }
  };

  useEffect(() => {
    void loadStatus();
    void loadVoiceOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validateDefinitions = async (): Promise<void> => {
    try {
      setValidatingDefinitions(true);
      setDefinitionValidationResult(null);
      const { data } = await adminAxios.post<TutorialDefinitionValidationResponse>(
        `${API_BASE}/admin/tutorials/definitions/validate`,
        {},
        { timeout: TUTORIAL_COMMAND_TIMEOUT_MS }
      );
      setDefinitionValidationResult(data);
      await loadStatus(tutorialId);
      if (data.status === 'succeeded') {
        message.success('Tutorial definitions validated.');
      } else {
        message.error('Tutorial definition validation failed. Review command output below.');
      }
    } catch (error: any) {
      message.error(String(error?.response?.data?.detail || error?.message || 'Failed to validate tutorial definitions.'));
    } finally {
      setValidatingDefinitions(false);
    }
  };

  const runGoldenFlow = async (): Promise<void> => {
    try {
      setRunning(true);
      setRunResult(null);
      setStoryboardResult(null);
      setRenderPackageResult(null);
      setVideoRenderResult(null);
      clearVideoPreview();
      const { data } = await adminAxios.post<TutorialRunResponse>(
        `${API_BASE}/admin/tutorials/golden-flow/run`,
        {
          tutorial: tutorialId,
          baseUrl,
          recordVideo,
        },
        { timeout: TUTORIAL_COMMAND_TIMEOUT_MS }
      );
      setRunResult(data);
      setStatus(data.summary);
      if (data.status === 'succeeded') {
        message.success(isGeneratedDocumentTutorial ? 'Document tutorial flow generated.' : 'Tutorial golden flow completed.');
      } else {
        message.error(
          isGeneratedDocumentTutorial
            ? 'Document tutorial flow generation failed. Review command output below.'
            : 'Tutorial golden flow failed. Review command output below.'
        );
      }
    } catch (error: any) {
      message.error(String(error?.response?.data?.detail || error?.message || 'Failed to run tutorial golden flow.'));
    } finally {
      setRunning(false);
    }
  };

  const generateStoryboard = async (): Promise<void> => {
    try {
      setStoryboarding(true);
      setStoryboardResult(null);
      setRenderPackageResult(null);
      setVideoRenderResult(null);
      clearVideoPreview();
      const { data } = await adminAxios.post<TutorialStoryboardResponse>(
        `${API_BASE}/admin/tutorials/storyboard/generate`,
        {
          tutorial: tutorialId,
        },
        { timeout: TUTORIAL_COMMAND_TIMEOUT_MS }
      );
      setStoryboardResult(data);
      setStatus(data.summary);
      if (data.status === 'succeeded') {
        message.success('Tutorial storyboard generated.');
      } else {
        message.error('Tutorial storyboard generation failed. Review command output below.');
      }
    } catch (error: any) {
      message.error(String(error?.response?.data?.detail || error?.message || 'Failed to generate tutorial storyboard.'));
    } finally {
      setStoryboarding(false);
    }
  };

  const buildRenderPackage = async (): Promise<void> => {
    try {
      setBuildingRenderPackage(true);
      setRenderPackageResult(null);
      setVideoRenderResult(null);
      clearVideoPreview();
      const { data } = await adminAxios.post<TutorialRenderPackageResponse>(
        `${API_BASE}/admin/tutorials/render-package/build`,
        {
          tutorial: tutorialId,
        },
        { timeout: TUTORIAL_COMMAND_TIMEOUT_MS }
      );
      setRenderPackageResult(data);
      setStatus(data.summary);
      if (data.status === 'succeeded') {
        message.success('Tutorial render package built.');
      } else {
        message.error('Tutorial render package build failed. Review command output below.');
      }
    } catch (error: any) {
      message.error(String(error?.response?.data?.detail || error?.message || 'Failed to build tutorial render package.'));
    } finally {
      setBuildingRenderPackage(false);
    }
  };

  const loadVideoPreviewForPath = async (path: string): Promise<void> => {
    try {
      setPreviewLoading(true);
      const response = await adminAxios.get(`${API_BASE}/admin/tutorials/golden-flow/artifact`, {
        params: { tutorial: tutorialId, path, cacheBust: Date.now() },
        responseType: 'blob',
      });
      const blob = response.data instanceof Blob
        ? response.data
        : new Blob([response.data], { type: 'video/mp4' });
      const url = window.URL.createObjectURL(blob);
      setPreviewVideoUrl((currentUrl) => {
        if (currentUrl) {
          window.URL.revokeObjectURL(currentUrl);
        }
        return url;
      });
      setPreviewVideoPath(path);
    } catch (error: any) {
      message.error(String(error?.response?.data?.detail || error?.message || 'Failed to load video preview.'));
    } finally {
      setPreviewLoading(false);
    }
  };

  const renderVideo = async (): Promise<void> => {
    try {
      setRenderingVideo(true);
      setRunning(true);
      setStoryboarding(true);
      setBuildingRenderPackage(true);
      setRunResult(null);
      setStoryboardResult(null);
      setRenderPackageResult(null);
      setVideoRenderResult(null);
      clearVideoPreview();

      const runResponse = await adminAxios.post<TutorialRunResponse>(
        `${API_BASE}/admin/tutorials/golden-flow/run`,
        {
          tutorial: tutorialId,
          baseUrl,
          recordVideo,
        },
        { timeout: TUTORIAL_COMMAND_TIMEOUT_MS }
      );
      setRunResult(runResponse.data);
      setStatus(runResponse.data.summary);
      if (runResponse.data.status !== 'succeeded') {
        message.error(
          isGeneratedDocumentTutorial
            ? 'Document tutorial flow generation failed. Review command output below.'
            : 'Tutorial golden flow failed. Review command output below.'
        );
        return;
      }
      setRunning(false);

      const storyboardResponse = await adminAxios.post<TutorialStoryboardResponse>(
        `${API_BASE}/admin/tutorials/storyboard/generate`,
        {
          tutorial: tutorialId,
        },
        { timeout: TUTORIAL_COMMAND_TIMEOUT_MS }
      );
      setStoryboardResult(storyboardResponse.data);
      setStatus(storyboardResponse.data.summary);
      if (storyboardResponse.data.status !== 'succeeded') {
        message.error('Tutorial storyboard generation failed. Review command output below.');
        return;
      }
      setStoryboarding(false);

      const renderPackageResponse = await adminAxios.post<TutorialRenderPackageResponse>(
        `${API_BASE}/admin/tutorials/render-package/build`,
        {
          tutorial: tutorialId,
        },
        { timeout: TUTORIAL_COMMAND_TIMEOUT_MS }
      );
      setRenderPackageResult(renderPackageResponse.data);
      setStatus(renderPackageResponse.data.summary);
      if (renderPackageResponse.data.status !== 'succeeded') {
        message.error('Tutorial render package build failed. Review command output below.');
        return;
      }
      setBuildingRenderPackage(false);

      const { data } = await adminAxios.post<TutorialVideoRenderResponse>(
        `${API_BASE}/admin/tutorials/video/render`,
        {
          tutorial: tutorialId,
          voiceId: selectedVoiceId || undefined,
        },
        { timeout: TUTORIAL_COMMAND_TIMEOUT_MS }
      );
      setVideoRenderResult(data);
      setStatus(data.summary);
      if (data.status === 'succeeded') {
        message.success('Tutorial video rebuilt and rendered.');
        const renderedPath = data.summary.renderResult?.output;
        if (renderedPath) {
          await loadVideoPreviewForPath(renderedPath);
        }
      } else {
        message.error('Tutorial video render failed. Review command output below.');
      }
    } catch (error: any) {
      message.error(String(error?.response?.data?.detail || error?.message || 'Failed to rebuild and render tutorial video.'));
    } finally {
      setRunning(false);
      setStoryboarding(false);
      setBuildingRenderPackage(false);
      setRenderingVideo(false);
    }
  };

  const downloadArtifact = async (artifact: TutorialArtifact): Promise<void> => {
    try {
      setDownloadingPath(artifact.path);
      const response = await adminAxios.get(`${API_BASE}/admin/tutorials/golden-flow/artifact`, {
        params: { tutorial: tutorialId, path: artifact.path },
        responseType: 'blob',
      });
      downloadBlob(response.data, artifact.path.split('/').pop() || 'tutorial-artifact');
    } catch (error: any) {
      message.error(String(error?.response?.data?.detail || error?.message || 'Failed to download artifact.'));
    } finally {
      setDownloadingPath(null);
    }
  };

  const loadVideoPreview = async (): Promise<void> => {
    if (!finalVideoPath) {
      message.warning('No rendered video path is available yet.');
      return;
    }
    await loadVideoPreviewForPath(finalVideoPath);
  };

  const artifactColumns: ColumnsType<TutorialArtifact> = [
    {
      title: 'Artifact',
      dataIndex: 'path',
      key: 'path',
      render: (value: string) => <Text code>{value}</Text>,
    },
    {
      title: 'Size',
      dataIndex: 'sizeBytes',
      key: 'sizeBytes',
      width: 120,
      render: (value: number) => formatBytes(value),
    },
    {
      title: 'Modified',
      dataIndex: 'modifiedAt',
      key: 'modifiedAt',
      width: 220,
      render: (value: string) => new Date(value).toLocaleString(),
    },
    {
      title: '',
      key: 'actions',
      width: 130,
      render: (_, artifact) => (
        <Button
          size="small"
          onClick={() => void downloadArtifact(artifact)}
          loading={downloadingPath === artifact.path}
        >
          Download
        </Button>
      ),
    },
  ];

  const flowColumns: ColumnsType<TutorialFlowSummary> = [
    {
      title: 'Tutorial',
      key: 'tutorial',
      render: (_, flow) => (
        <Space direction="vertical" size={0}>
          <Text strong>{flow.title || flow.id}</Text>
          <Text code>{flow.id}</Text>
        </Space>
      ),
    },
    {
      title: 'Fixture',
      dataIndex: 'fixture',
      key: 'fixture',
      render: (value: string) => <Text>{value}</Text>,
    },
    {
      title: 'Scenes',
      dataIndex: 'sceneCount',
      key: 'sceneCount',
      width: 90,
    },
    {
      title: 'Artifacts',
      key: 'artifacts',
      render: (_, flow) => (
        <Space wrap size={[4, 4]}>
          <Tag color={statusTagColor(flow.traceStatus)}>Trace: {flow.traceStatus || 'missing'}</Tag>
          <Tag color={statusTagColor(flow.storyboardStatus)}>Storyboard: {flow.storyboardStatus || 'missing'}</Tag>
          <Tag color={statusTagColor(flow.renderManifestStatus)}>Render: {flow.renderManifestStatus || 'missing'}</Tag>
          <Tag color={statusTagColor(flow.voiceoverStatus)}>TTS: {flow.voiceoverStatus || 'missing'}</Tag>
          <Tag color={statusTagColor(flow.videoRenderStatus)}>Video: {flow.videoRenderStatus || 'missing'}</Tag>
        </Space>
      ),
    },
    {
      title: 'Last Run',
      dataIndex: 'lastRunAt',
      key: 'lastRunAt',
      width: 190,
      render: (value?: string | null) => (value ? new Date(value).toLocaleString() : <Text type="secondary">Never</Text>),
    },
    {
      title: '',
      key: 'actions',
      width: 110,
      render: (_, flow) => (
        <Button
          size="small"
          type={flow.id === tutorialId ? 'primary' : 'default'}
          onClick={() => {
            const nextTutorial = flow.id || tutorialId;
            setTutorialId(nextTutorial);
            void loadStatus(nextTutorial);
          }}
        >
          Select
        </Button>
      ),
    },
  ];

  const runStatus = runResult?.status || status?.traceStatus || 'not-run';
  const statusColor = runStatus === 'succeeded' ? 'green' : runStatus === 'failed' ? 'red' : 'default';
  const storyboardStatus = storyboardResult?.status || status?.storyboardStatus || 'not-generated';
  const storyboardColor = storyboardStatus === 'succeeded' || storyboardStatus === 'draft' ? 'green' : storyboardStatus === 'failed' ? 'red' : 'default';
  const renderPackageStatus = renderPackageResult?.status || status?.renderManifestStatus || 'not-built';
  const renderPackageColor = renderPackageStatus === 'succeeded' || renderPackageStatus === 'ready' ? 'green' : renderPackageStatus === 'failed' ? 'red' : 'default';
  const voiceoverStatus = status?.voiceoverStatus || 'not-synthesized';
  const voiceoverColor = voiceoverStatus === 'succeeded' ? 'green' : voiceoverStatus === 'failed' ? 'red' : 'default';
  const videoStatus = videoRenderResult?.status || status?.videoRenderStatus || 'not-rendered';
  const videoColor = videoStatus === 'succeeded' ? 'green' : videoStatus === 'failed' ? 'red' : 'default';

  return (
    <div className="admin-dashboard-surface">
      <Card title="Tutorial Golden Flow" className="admin-dashboard-surface-card">
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <div>
            <Title level={4} style={{ marginBottom: 8 }}>
              Deterministic Tutorial Pipeline
            </Title>
            <Paragraph style={{ marginBottom: 0 }}>
              Runs a verified tutorial flow from the admin backend, validates artifacts, then turns the trace and
              screenshots into storyboard and narration draft files for download. App flows use Playwright against the
              customer UI; generated document flows create deterministic source screenshots without a browser.
            </Paragraph>
          </div>

          <Alert
            type="info"
            showIcon
            message={isGeneratedDocumentTutorial ? 'Generated document tutorial' : 'Backend-run browser automation'}
            description={
              isGeneratedDocumentTutorial
                ? 'This tutorial creates a sample doc x file and Word-style screenshots from scripted scenes, so it does not need the local frontend or Playwright.'
                : 'The backend must be able to launch Node and Playwright, and the base URL must point at a running frontend. Failures are returned as command output instead of being hidden.'
            }
          />

          {status?.timingDiagnostics?.error && (
            <Alert
              type="error"
              showIcon
              message="Last golden flow error"
              description={<pre style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{status.timingDiagnostics.error}</pre>}
            />
          )}

          <Space wrap size="middle">
            <Select
              showSearch
              value={tutorialId}
              onChange={(value) => {
                setTutorialId(value);
                void loadStatus(value);
              }}
              options={tutorialSelectOptions.length ? tutorialSelectOptions : [{ value: tutorialId, label: tutorialId }]}
              optionFilterProp="label"
              style={{ width: 360 }}
            />
            <Input
              addonBefore="Base URL"
              value={baseUrl}
              onChange={(event) => setBaseUrl(event.target.value)}
              disabled={isGeneratedDocumentTutorial}
              style={{ width: 420 }}
            />
            <Checkbox
              checked={recordVideo}
              disabled={isGeneratedDocumentTutorial}
              onChange={(event) => setRecordVideo(event.target.checked)}
            >
              Record raw video
            </Checkbox>
          </Space>

          <Space wrap size="middle" align="start">
            <Select
              showSearch
              allowClear
              placeholder="ElevenLabs voice"
              value={selectedVoiceId || undefined}
              onChange={(value) => setSelectedVoiceId(value || '')}
              options={voiceSelectOptions}
              optionFilterProp="label"
              loading={voicesLoading}
              style={{ width: 420 }}
            />
            <Input
              addonBefore="Voice ID"
              value={selectedVoiceId}
              onChange={(event) => setSelectedVoiceId(event.target.value)}
              placeholder="Use an ElevenLabs voice_id"
              style={{ width: 360 }}
            />
            <Button onClick={() => void loadVoiceOptions()} loading={voicesLoading}>
              Refresh Voices
            </Button>
            {selectedVoice?.previewUrl && (
              <audio controls preload="none" src={selectedVoice.previewUrl} style={{ height: 32 }} />
            )}
            {selectedVoice && (
              <Text type="secondary">
                {selectedVoice.name || selectedVoice.voiceId}
                {selectedVoice.category ? ` · ${selectedVoice.category}` : ''}
              </Text>
            )}
          </Space>

          {voiceLoadError && (
            <Alert
              type="warning"
              showIcon
              message="Voice choices unavailable"
              description={voiceLoadError}
            />
          )}

          <Space wrap>
            <Button type="primary" onClick={() => void runGoldenFlow()} loading={running}>
              {runActionLabel}
            </Button>
            <Button
              onClick={() => void generateStoryboard()}
              loading={storyboarding}
              disabled={!status || status.traceStatus !== 'succeeded'}
            >
              Generate Storyboard
            </Button>
            <Button
              onClick={() => void buildRenderPackage()}
              loading={buildingRenderPackage}
              disabled={!status || status.storyboardStatus !== 'draft'}
            >
              Build Render Package
            </Button>
            <Button
              onClick={() => void renderVideo()}
              loading={renderingVideo}
              disabled={!status}
            >
              Rebuild + Render Video
            </Button>
            <Button onClick={() => void loadStatus()} loading={loading}>
              Refresh Status
            </Button>
            <Button onClick={() => void validateDefinitions()} loading={validatingDefinitions}>
              Validate Definitions
            </Button>
            <Tag color={statusColor}>{runStatus}</Tag>
            <Tag color={storyboardColor}>Storyboard: {storyboardStatus}</Tag>
            <Tag color={renderPackageColor}>Render: {renderPackageStatus}</Tag>
            <Tag color={voiceoverColor}>TTS: {voiceoverStatus}</Tag>
            <Tag color={videoColor}>Video: {videoStatus}</Tag>
            {selectedFlow && (
              <Text type="secondary">
                {selectedFlow.title} · {selectedFlow.sceneCount} scenes
                {selectedFlow.source ? ` · ${selectedFlow.source}` : ''} · {selectedFlow.route}
              </Text>
            )}
          </Space>
        </Space>
      </Card>

      {status && (
        <Card
          title="Tutorial Library"
          className="admin-dashboard-surface-card"
          extra={
            <Button
              size="small"
              aria-expanded={tutorialLibraryExpanded}
              aria-controls="admin-tutorial-library-content"
              onClick={() => setTutorialLibraryExpanded((value) => !value)}
            >
              {tutorialLibraryExpanded ? 'Hide Library' : 'Show Library'}
            </Button>
          }
        >
          {tutorialLibraryExpanded && (
            <Space
              id="admin-tutorial-library-content"
              direction="vertical"
              size="middle"
              style={{ width: '100%' }}
            >
              <Paragraph style={{ marginBottom: 0 }}>
                These are the core customer tutorial flows currently defined for automated capture. Select a row to run,
                regenerate TTS/video, preview, or inspect artifacts for that tutorial.
              </Paragraph>

              {definitionValidationResult && (
                <Alert
                  type={definitionValidationResult.status === 'succeeded' ? 'success' : 'error'}
                  showIcon
                  message={
                    definitionValidationResult.status === 'succeeded'
                      ? 'Definition validation succeeded'
                      : 'Definition validation failed'
                  }
                  description={
                    <pre style={{ whiteSpace: 'pre-wrap', margin: 0, maxHeight: 180, overflow: 'auto' }}>
                      {[definitionValidationResult.validation.stdout, definitionValidationResult.validation.stderr]
                        .filter(Boolean)
                        .join('\n')}
                    </pre>
                  }
                />
              )}

              <Table
                size="small"
                rowKey={(flow) => flow.id || flow.title || flow.route || 'tutorial-flow'}
                columns={flowColumns}
                dataSource={status.flows}
                pagination={false}
                scroll={{ x: 1100 }}
                locale={{ emptyText: 'No tutorial flows are defined yet.' }}
              />
            </Space>
          )}
        </Card>
      )}

      {status && (
        <Card title="Review Queue" className="admin-dashboard-surface-card">
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Paragraph style={{ marginBottom: 0 }}>
              Start with these artifacts. The full table below is still available for debugging, but most review passes
              should only need the final video, script, narration, TTS manifest, and render result.
            </Paragraph>

            <div className="admin-tutorial-review-list">
              {reviewItems.map((item) => {
                const artifact = artifactsByPath.get(item.path);
                const isReady = Boolean(artifact);
                return (
                  <div className={`admin-tutorial-review-row${item.primary ? ' is-primary' : ''}`} key={item.key}>
                    <div className="admin-tutorial-review-main">
                      <Space size="small" wrap>
                        <Text strong>{item.label}</Text>
                        {item.newArtifact && <Tag color="blue">New</Tag>}
                        <Tag color={isReady ? 'green' : 'default'}>{isReady ? 'Ready' : 'Missing'}</Tag>
                      </Space>
                      <Text code className="admin-tutorial-review-path">{item.path}</Text>
                    </div>
                    <Text type="secondary" className="admin-tutorial-review-purpose">
                      {item.purpose}
                    </Text>
                    <Space className="admin-tutorial-review-actions" wrap>
                      {item.previewable && (
                        <Button
                          type="primary"
                          size="small"
                          onClick={() => void loadVideoPreview()}
                          loading={previewLoading}
                          disabled={!isReady}
                        >
                          Preview
                        </Button>
                      )}
                      <Button
                        size="small"
                        onClick={() => artifact && void downloadArtifact(artifact)}
                        loading={downloadingPath === item.path}
                        disabled={!artifact}
                      >
                        Download
                      </Button>
                    </Space>
                  </div>
                );
              })}
            </div>

            {previewVideoUrl ? (
              <div className="admin-tutorial-preview">
                <div className="admin-tutorial-preview-header">
                  <Text strong>Browser Preview</Text>
                  {previewVideoPath && <Text code>{previewVideoPath}</Text>}
                </div>
                <video
                  className="admin-tutorial-preview-video"
                  controls
                  preload="metadata"
                  src={previewVideoUrl}
                />
              </div>
            ) : (
              <Alert
                type="info"
                showIcon
                message="Video preview"
                description="Render the tutorial, then click Preview on the final video row to watch it here without saving a local download."
              />
            )}
          </Space>
        </Card>
      )}

      {status && (
        <Card title="Latest Artifacts" className="admin-dashboard-surface-card">
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Space wrap>
              <Tag color={status.runnerAvailable ? 'green' : 'red'}>Runner</Tag>
              <Tag color={status.validatorAvailable ? 'green' : 'red'}>Validator</Tag>
              <Tag color={status.storyboardGeneratorAvailable ? 'green' : 'red'}>Storyboard Generator</Tag>
              <Tag color={status.renderManifestBuilderAvailable ? 'green' : 'red'}>Render Builder</Tag>
              <Tag color={status.videoRendererAvailable ? 'green' : 'red'}>Video Renderer</Tag>
              <Tag color={status.manifestAvailable ? 'green' : 'red'}>Manifest</Tag>
              <Tag color={status.flowAvailable ? 'green' : 'red'}>Flow JSON</Tag>
              {typeof status.trace?.duration_ms === 'number' && (
                <Text type="secondary">Duration: {status.trace.duration_ms} ms</Text>
              )}
              {typeof status.storyboard?.estimated_duration_seconds === 'number' && (
                <Text type="secondary">Storyboard: {status.storyboard.estimated_duration_seconds} sec</Text>
              )}
              {typeof status.renderManifest?.duration_seconds === 'number' && (
                <Text type="secondary">Render package: {status.renderManifest.duration_seconds} sec</Text>
              )}
              {status.voiceover?.provider && (
                <Text type="secondary">
                  TTS: {status.voiceover.provider}
                  {status.voiceover.model_id ? ` · ${status.voiceover.model_id}` : ''}
                  {status.voiceover.voice_id ? ` · voice ${status.voiceover.voice_id}` : ''}
                  {typeof status.voiceover.track_count === 'number'
                    ? ` · ${status.voiceover.track_count} tracks`
                    : ''}
                </Text>
              )}
              {status.renderResult?.output && (
                <Text type="secondary">
                  Video: {status.renderResult.output}
                  {typeof status.renderResult.output_size_bytes === 'number'
                    ? ` (${formatBytes(status.renderResult.output_size_bytes)})`
                    : ''}
                </Text>
              )}
            </Space>

            <Table
              size="small"
              rowKey="path"
              columns={artifactColumns}
              dataSource={status.artifacts}
              pagination={false}
              locale={{ emptyText: 'No artifacts have been generated yet.' }}
            />
          </Space>
        </Card>
      )}

      {videoRenderResult && (
        <Card title="Video Render Output" className="admin-dashboard-surface-card">
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <div>
              <Text strong>Video render command</Text>
              <pre style={{ whiteSpace: 'pre-wrap' }}>{videoRenderResult.render.command.join(' ')}</pre>
              <Text type={videoRenderResult.render.returnCode === 0 ? 'success' : 'danger'}>
                Exit code {videoRenderResult.render.returnCode}
              </Text>
              <pre style={{ whiteSpace: 'pre-wrap', maxHeight: 220, overflow: 'auto' }}>
                {[videoRenderResult.render.stdout, videoRenderResult.render.stderr].filter(Boolean).join('\n')}
              </pre>
            </div>

            {videoRenderResult.validation && (
              <div>
                <Text strong>Validator command</Text>
                <pre style={{ whiteSpace: 'pre-wrap' }}>{videoRenderResult.validation.command.join(' ')}</pre>
                <Text type={videoRenderResult.validation.returnCode === 0 ? 'success' : 'danger'}>
                  Exit code {videoRenderResult.validation.returnCode}
                </Text>
                <pre style={{ whiteSpace: 'pre-wrap', maxHeight: 220, overflow: 'auto' }}>
                  {[videoRenderResult.validation.stdout, videoRenderResult.validation.stderr].filter(Boolean).join('\n')}
                </pre>
              </div>
            )}
          </Space>
        </Card>
      )}

      {renderPackageResult && (
        <Card title="Render Package Output" className="admin-dashboard-surface-card">
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <div>
              <Text strong>Render package command</Text>
              <pre style={{ whiteSpace: 'pre-wrap' }}>{renderPackageResult.renderPackage.command.join(' ')}</pre>
              <Text type={renderPackageResult.renderPackage.returnCode === 0 ? 'success' : 'danger'}>
                Exit code {renderPackageResult.renderPackage.returnCode}
              </Text>
              <pre style={{ whiteSpace: 'pre-wrap', maxHeight: 220, overflow: 'auto' }}>
                {[renderPackageResult.renderPackage.stdout, renderPackageResult.renderPackage.stderr]
                  .filter(Boolean)
                  .join('\n')}
              </pre>
            </div>

            {renderPackageResult.validation && (
              <div>
                <Text strong>Validator command</Text>
                <pre style={{ whiteSpace: 'pre-wrap' }}>{renderPackageResult.validation.command.join(' ')}</pre>
                <Text type={renderPackageResult.validation.returnCode === 0 ? 'success' : 'danger'}>
                  Exit code {renderPackageResult.validation.returnCode}
                </Text>
                <pre style={{ whiteSpace: 'pre-wrap', maxHeight: 220, overflow: 'auto' }}>
                  {[renderPackageResult.validation.stdout, renderPackageResult.validation.stderr]
                    .filter(Boolean)
                    .join('\n')}
                </pre>
              </div>
            )}
          </Space>
        </Card>
      )}

      {storyboardResult && (
        <Card title="Storyboard Output" className="admin-dashboard-surface-card">
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <div>
              <Text strong>Storyboard command</Text>
              <pre style={{ whiteSpace: 'pre-wrap' }}>{storyboardResult.storyboard.command.join(' ')}</pre>
              <Text type={storyboardResult.storyboard.returnCode === 0 ? 'success' : 'danger'}>
                Exit code {storyboardResult.storyboard.returnCode}
              </Text>
              <pre style={{ whiteSpace: 'pre-wrap', maxHeight: 220, overflow: 'auto' }}>
                {[storyboardResult.storyboard.stdout, storyboardResult.storyboard.stderr].filter(Boolean).join('\n')}
              </pre>
            </div>

            {storyboardResult.validation && (
              <div>
                <Text strong>Validator command</Text>
                <pre style={{ whiteSpace: 'pre-wrap' }}>{storyboardResult.validation.command.join(' ')}</pre>
                <Text type={storyboardResult.validation.returnCode === 0 ? 'success' : 'danger'}>
                  Exit code {storyboardResult.validation.returnCode}
                </Text>
                <pre style={{ whiteSpace: 'pre-wrap', maxHeight: 220, overflow: 'auto' }}>
                  {[storyboardResult.validation.stdout, storyboardResult.validation.stderr].filter(Boolean).join('\n')}
                </pre>
              </div>
            )}
          </Space>
        </Card>
      )}

      {runResult && (
        <Card title="Command Output" className="admin-dashboard-surface-card">
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <div>
              <Text strong>Runner command</Text>
              <pre style={{ whiteSpace: 'pre-wrap' }}>{runResult.run.command.join(' ')}</pre>
              <Text type={runResult.run.returnCode === 0 ? 'success' : 'danger'}>
                Exit code {runResult.run.returnCode}
              </Text>
              <pre style={{ whiteSpace: 'pre-wrap', maxHeight: 220, overflow: 'auto' }}>
                {[runResult.run.stdout, runResult.run.stderr].filter(Boolean).join('\n')}
              </pre>
            </div>

            {runResult.validation && (
              <div>
                <Text strong>Validator command</Text>
                <pre style={{ whiteSpace: 'pre-wrap' }}>{runResult.validation.command.join(' ')}</pre>
                <Text type={runResult.validation.returnCode === 0 ? 'success' : 'danger'}>
                  Exit code {runResult.validation.returnCode}
                </Text>
                <pre style={{ whiteSpace: 'pre-wrap', maxHeight: 220, overflow: 'auto' }}>
                  {[runResult.validation.stdout, runResult.validation.stderr].filter(Boolean).join('\n')}
                </pre>
              </div>
            )}
          </Space>
        </Card>
      )}
    </div>
  );
};

export default AdminTutorialsTab;
